import { Field, RichTextField, TextField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import {
  Button,
  ComponentProps,
  getContrastThemeClass,
  JabraIcon,
  resolveBackgroundStyle,
} from '@gn-audio/jss-components';

import { useSpotTracking } from 'utils/hooks/useSpotTracking';

import { Eyebrow } from '../../../common/components/Eyebrow';
import PromoItem from './DualColumnPromoItem';
import {
  DualColumnPromoItem,
  FirstButtonSettings,
  SecondButtonSettings,
} from './dualColumnPromoTypes';

export type DualColumnPromoSpot = {
  eyebrowText: TextField;
  headline: RichTextField;
  description: RichTextField;
  backgroundColor: TextField;
  firstButtonIcon: Field<keyof typeof icons>;
  secondButtonIcon: Field<keyof typeof icons>;
  promoItems?: DualColumnPromoItem[];
  trackingSpotName?: Field<string>;
  firstCtaButtonTrackingSpotName?: Field<string>;
  secondCtaButtonTrackingSpotName?: Field<string>;
} & FirstButtonSettings &
  SecondButtonSettings;

export type DualColumnPromoSpotProps = {
  fields: DualColumnPromoSpot;
} & ComponentProps;

const DualColumnPromoSpot: React.FC<DualColumnPromoSpotProps> = ({ fields }) => {
  const {
    eyebrowText,
    headline,
    description,
    backgroundColor,
    firstButtonLink,
    firstButtonText,
    firstButtonVariant,
    firstButtonIcon,
    firstButtonAriaLabel,
    secondButtonLink,
    secondButtonText,
    secondButtonVariant,
    secondButtonIcon,
    secondButtonAriaLabel,
    promoItems,
    trackingSpotName,
    firstCtaButtonTrackingSpotName,
    secondCtaButtonTrackingSpotName,
  } = fields;

  const spotThemeClass = getContrastThemeClass(backgroundColor.value as string);
  const spotBackgroundStyle = resolveBackgroundStyle(backgroundColor.value as string);
  const isAnyMainButtonPresent =
    (!!firstButtonLink.value?.href && !!firstButtonText.value) ||
    (!!secondButtonLink.value?.href && !!secondButtonText.value);
  const isContentPresentAbovePromoItems =
    !!eyebrowText.value || !!headline.value || !!description.value || isAnyMainButtonPresent;

  const spotRef = useSpotTracking(trackingSpotName?.value);

  return (
    <div
      ref={spotRef}
      data-wt-spot={trackingSpotName?.value}
      style={spotBackgroundStyle}
      className={spotThemeClass}
    >
      <article className="container flex flex-col items-start">
        <div className="w-full text-center">
          <Eyebrow className="mb-6" text={eyebrowText.value as string} />
          {!!headline.value && (
            <h2
              className="heading-2 mb-6"
              dangerouslySetInnerHTML={{ __html: sanitize(headline.value as string) }}
            />
          )}
          {!!description.value && (
            <p
              className="paragraph-large mb-6"
              dangerouslySetInnerHTML={{ __html: sanitize(description.value as string) }}
            />
          )}
          {isAnyMainButtonPresent && (
            <section className="mb-6 flex flex-col flex-wrap items-center justify-center gap-4 md:flex-row">
              {!!firstButtonLink.value?.href && !!firstButtonText.value && (
                <Button
                  asLink
                  href={firstButtonLink.value.href}
                  size="m"
                  {...(firstButtonAriaLabel?.value && {
                    'aria-label': firstButtonAriaLabel.value,
                  })}
                  variant={firstButtonVariant.value || 'primary'}
                  {...(firstButtonIcon?.value && {
                    icon: <JabraIcon Icon={icons?.[firstButtonIcon.value]} />,
                    iconPlacement: 'right',
                  })}
                  {...(firstCtaButtonTrackingSpotName?.value && {
                    'data-wt-name': firstCtaButtonTrackingSpotName.value,
                  })}
                >
                  {firstButtonText.value}
                </Button>
              )}
              {!!secondButtonLink.value?.href && !!secondButtonText.value && (
                <Button
                  asLink
                  href={secondButtonLink.value.href}
                  size="m"
                  {...(secondButtonAriaLabel?.value && {
                    'aria-label': secondButtonAriaLabel.value,
                  })}
                  variant={secondButtonVariant.value || 'secondary'}
                  {...(secondButtonIcon?.value && {
                    icon: <JabraIcon Icon={icons?.[secondButtonIcon.value]} />,
                    iconPlacement: 'right',
                  })}
                  {...(secondCtaButtonTrackingSpotName?.value && {
                    'data-wt-name': secondCtaButtonTrackingSpotName.value,
                  })}
                >
                  {secondButtonText.value}
                </Button>
              )}
            </section>
          )}
        </div>
        {!!promoItems?.length && (
          <div
            className={classNames('flex w-full flex-col gap-8 md:grid md:grid-cols-2', {
              'mt-6': isContentPresentAbovePromoItems,
            })}
          >
            {promoItems.map((item) => (
              <PromoItem key={item.id} item={item} />
            ))}
          </div>
        )}
      </article>
    </div>
  );
};
export default DualColumnPromoSpot;
